/**
 * <MePlanPage> - Iter 9.40 Pass C unified /me/plan
 *
 * Single entry point for clients to plan an experience. Two clear modes:
 *
 *   1. Service     → pick ONE category (makeup, hair, etc.). Routes to the
 *                    category landing or the host-finder for that service.
 *   2. Occasion    → pick a curated bundle (Wedding, Photoshoot, Birthday...)
 *                    surfaced from db.master_experiences. Selecting an
 *                    occasion creates a draft experience and pre-selects all
 *                    services bundled into it.
 *
 * Replaces the old hardcoded `EVENT_TYPES` array in /app/frontend/src/pages/PlanExperience.jsx.
 * That legacy route is preserved at /plan-experience for back-compat.
 *
 * Backend: GET /api/me/plan/options.
 */
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { ArrowLeft, Sparkles, Calendar, ChevronRight, Loader2 } from "lucide-react";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function MePlanPage() {
  const navigate = useNavigate();
  const [options, setOptions] = useState(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const token = localStorage.getItem("session_token");
        const res = await fetch(`${API}/me/plan/options`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (res.ok) setOptions(await res.json());
      } catch { /* silent */ }
      setLoading(false);
    })();
  }, []);

  const startService = async (cat) => {
    if (cat.launch_state === "waitlist") {
      navigate(`/join?category=${cat.category_id}`);
      return;
    }
    // Retail-as-look branch - Style category goes to Save-a-look flow.
    if (cat.category_id === "style") {
      navigate(`/me/plan/look?category=${cat.category_id}`);
      return;
    }
    // Route to /me/preferences#<cat> so the user can prep before sharing
    navigate(`/me/preferences?focus=${cat.category_id}`);
  };

  const startOccasion = async (occasion) => {
    setSubmitting(true);
    try {
      const token = localStorage.getItem("session_token");
      // Iter 9.40 Pass C - Unified /api/me/plan/occasion/start.
      // Creates a personal_event + one draft preference_card per default category.
      const res = await fetch(`${API}/me/plan/occasion/start`, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" },
        body: JSON.stringify({
          experience_id: occasion.experience_id,
          name: occasion.name,
        }),
      });
      if (res.ok) {
        const data = await res.json();
        const n = (data.linked_card_ids || []).length;
        toast.success(`${occasion.name} draft created - ${n} preference card${n === 1 ? "" : "s"}`);
        navigate("/me/occasions");
      } else {
        toast.info(`Continue planning your ${occasion.name}`);
        navigate(`/me?plan=${occasion.experience_id}`);
      }
    } catch {
      navigate(`/me?plan=${occasion.experience_id}`);
    }
    setSubmitting(false);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center" data-testid="me-plan-loading">
        <Loader2 className="w-6 h-6 animate-spin text-gray-400" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 pb-24" data-testid="me-plan-page">
      <header className="bg-white border-b border-gray-100 sticky top-0 z-40">
        <div className="max-w-2xl mx-auto px-4 py-3 flex items-center gap-3">
          <button onClick={() => navigate("/me")} className="p-2 -ml-2 hover:bg-gray-100 rounded-full" data-testid="me-plan-back">
            <ArrowLeft className="w-5 h-5" />
          </button>
          <div>
            <h1 className="font-semibold text-base">Plan an experience</h1>
            <p className="text-xs text-gray-600">Pick a single service or a full occasion bundle.</p>
          </div>
        </div>
      </header>

      <main className="max-w-2xl mx-auto px-4 py-6">
        <Tabs defaultValue="service" className="space-y-5">
          <TabsList className="grid grid-cols-2 w-full" data-testid="me-plan-tabs">
            <TabsTrigger value="service" data-testid="me-plan-tab-service">
              <Sparkles className="w-4 h-4 mr-1.5" /> Single service
            </TabsTrigger>
            <TabsTrigger value="occasion" data-testid="me-plan-tab-occasion">
              <Calendar className="w-4 h-4 mr-1.5" /> Occasion bundle
            </TabsTrigger>
          </TabsList>

          {/* Single Service mode */}
          <TabsContent value="service" className="space-y-3">
            <p className="text-sm text-gray-500">Pick one category to share your preferences and find a host.</p>
            <div className="grid grid-cols-2 gap-3">
              {(options?.categories || []).map((c) => (
                <button
                  key={c.category_id}
                  type="button"
                  onClick={() => startService(c)}
                  data-testid={`me-plan-cat-${c.category_id}`}
                  className="text-left rounded-xl border border-gray-200 bg-white p-4 hover:shadow-md transition-all relative overflow-hidden"
                  style={c.color_primary ? { borderColor: `${c.color_primary}33` } : {}}
                >
                  <div className="flex items-start justify-between">
                    <div>
                      <div className="font-medium text-sm">{c.label}</div>
                      {c.launch_state === "waitlist" && (
                        <Badge variant="outline" className="text-[10px] mt-1">Waitlist</Badge>
                      )}
                      {c.launch_state === "pilot" && (
                        <Badge variant="outline" className="text-[10px] mt-1 border-blue-300 text-blue-800">Pilot</Badge>
                      )}
                    </div>
                    <ChevronRight className="w-4 h-4 text-gray-300 mt-0.5" />
                  </div>
                  {c.color_primary && (
                    <div
                      className="absolute -bottom-6 -right-6 w-16 h-16 rounded-full opacity-20"
                      style={{ backgroundColor: c.color_primary }}
                    />
                  )}
                </button>
              ))}
            </div>
          </TabsContent>

          {/* Occasion Bundle mode */}
          <TabsContent value="occasion" className="space-y-3">
            <p className="text-sm text-gray-500">Curated bundles - one click prepares all services for the occasion.</p>
            {(options?.occasions || []).length === 0 ? (
              <Card>
                <CardContent className="py-10 text-center text-sm text-gray-500" data-testid="me-plan-occasion-empty">
                  No occasion bundles published yet.
                </CardContent>
              </Card>
            ) : (
              <div className="space-y-3">
                {options.occasions.map((o) => (
                  <Card
                    key={o.experience_id}
                    className="hover:shadow-md transition-all cursor-pointer"
                    data-testid={`me-plan-occasion-${o.experience_id}`}
                    onClick={() => startOccasion(o)}
                  >
                    <CardContent className="p-4 flex items-center gap-3">
                      <div className="w-12 h-12 rounded-lg bg-gradient-to-br from-purple-100 to-pink-100 flex items-center justify-center flex-shrink-0">
                        {o.image_url ? (
                          <img src={o.image_url} alt={o.name} className="w-full h-full rounded-lg object-cover" />
                        ) : (
                          <Sparkles className="w-5 h-5 text-purple-500" />
                        )}
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="font-medium text-sm">{o.name}</div>
                        {o.description && <p className="text-xs text-gray-500 line-clamp-1">{o.description}</p>}
                        <div className="flex flex-wrap gap-1 mt-1.5">
                          {(o.default_categories || []).slice(0, 4).map((c) => (
                            <Badge key={c} variant="secondary" className="text-[10px] py-0 capitalize">{c}</Badge>
                          ))}
                          {(o.default_categories || []).length > 4 && (
                            <Badge variant="secondary" className="text-[10px] py-0">+{o.default_categories.length - 4}</Badge>
                          )}
                        </div>
                      </div>
                      <Button size="sm" variant="ghost" disabled={submitting} data-testid={`me-plan-occasion-start-${o.experience_id}`}>
                        Start <ChevronRight className="w-4 h-4 ml-1" />
                      </Button>
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
          </TabsContent>
        </Tabs>
      </main>
    </div>
  );
}
